import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { exportToExcel, exportToPDF } from '../../utils/export';
import { formatMoney, today } from '../../utils/format';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Loader from '../../components/common/Loader';
import { showError } from '../../utils/alert';

export default function TrialBalance() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.reports.trialBalance({ as_of: asOf });
      setData(res);
    } catch (err) {
      showError('Failed to load trial balance', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data ? (data.rows && data.rows.data ? data.rows.data : data.rows) || [] : [];
  const cols = [
    { key: 'name', label: 'Account' },
    { key: 'account_nature', label: 'Nature' },
    { key: 'debit', label: 'Debit', align: 'right', render: (r) => Number(r.debit) ? formatMoney(r.debit) : '' },
    { key: 'credit', label: 'Credit', align: 'right', render: (r) => Number(r.credit) ? formatMoney(r.credit) : '' },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Trial Balance"
        subtitle="Check debit and credit totals are balanced."
        crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Trial Balance' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => exportToPDF(rows, 'trial_balance_report', cols)} disabled={!rows.length}>PDF</button>
            <button className="btn btn-success" onClick={() => exportToExcel(rows, 'trial_balance_report', cols)} disabled={!rows.length}>Export Excel</button>
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <input className="form-control" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            <button className="btn btn-primary btn-sm" onClick={load}><FiRefreshCw /> Run</button>
          </div>

          {data && (
            <div className="stats-grid" style={{ marginBottom: 18 }}>
              <div className="stat-card"><div className="stat-icon indigo"><i className="bi bi-box-arrow-in-down" /></div><div><div className="stat-value">{formatMoney(data.total_debit)}</div><div className="stat-label">Total Debit</div></div></div>
              <div className="stat-card"><div className="stat-icon orange"><i className="bi bi-box-arrow-up" /></div><div><div className="stat-value">{formatMoney(data.total_credit)}</div><div className="stat-label">Total Credit</div></div></div>
              <div className="stat-card">
                <div className={`stat-icon ${data.balanced ? 'green' : 'red'}`}>{data.balanced ? <i className="bi bi-check-circle-fill" /> : <i className="bi bi-exclamation-triangle-fill" />}</div>
                <div><div className="stat-value">{data.balanced ? 'Balanced' : 'Not Balanced'}</div><div className="stat-label">Status</div></div>
              </div>
            </div>
          )}

          {loading ? <Loader text="Loading trial balance..." /> : (
            <DataTable data={rows} columns={cols} emptyTitle="No data" />
          )}
        </div>
      </div>
    </div>
  );
}